import React, { useMemo } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import { formatMoney } from '@dill/dill-shared';
import { openDillToast } from '../../../../utils/helpers';

const PlaidBalanceModal = ({
  open,
  handleClose,
  balanceDetails,
}: {
  open: boolean;
  handleClose: () => void;
  balanceDetails: any;
}) => {
  const currentBankDetails = useMemo(() => {
    if (balanceDetails?.accounts && balanceDetails?.accounts.length > 0) {
      return balanceDetails?.accounts[0];
    }
    return null;
  }, [balanceDetails]);

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Bank Details"
      maxWidth="480px"
      footer={<DillButton label="Close" variant="secondary" onClick={handleClose} />}>
      {currentBankDetails ? (
        <div className="flex flex-col gap-4">
          {/* Bank & account info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">
                Bank
              </p>
              <p className="text-sm font-medium text-gray-800">
                {balanceDetails?.item?.institution_name ?? '—'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">
                Account
              </p>
              <p className="text-sm font-medium text-gray-800">{currentBankDetails?.name ?? '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">
                Account Number
              </p>
              <p className="text-sm font-medium text-gray-800 tracking-widest">
                ••••{currentBankDetails?.mask}
              </p>
            </div>
          </div>

          {/* Balances */}
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-blue-100 bg-blue-50 rounded-xl p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-400 mb-0.5">
                Current Balance
              </p>
              <p className="text-lg font-semibold text-blue-700">
                {formatMoney(currentBankDetails?.balances?.current ?? 0, 2)}
              </p>
            </div>
            <div className="border border-green-100 bg-green-50 rounded-xl p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-green-500 mb-0.5">
                Available Balance
              </p>
              <p className="text-lg font-semibold text-green-700">
                {formatMoney(currentBankDetails?.balances?.available ?? 0, 2)}
              </p>
            </div>
          </div>

          {/* Full account number request */}
          <div className="flex items-center justify-between border border-gray-100 rounded-xl px-3 py-2.5 bg-gray-50">
            <p className="text-xs text-gray-500">Want the full account number?</p>
            <DillButton
              label="Request Access"
              variant="ghost-blue"
              onClick={() => {
                openDillToast({
                  message: `An email has been sent to our support team to request account number`,
                  type: 'SUCCESS',
                });
              }}
            />
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-6">No account details available.</p>
      )}
    </DillModal>
  );
};

export default PlaidBalanceModal;
